const pickerSelector = "[data-rcd-multi-select], [data-multi-person-picker]";
const optionSelector = "[data-rcd-multi-select-option], [data-person-option]";

const escapeHtml = (value) => String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");

const resolveFieldName = (picker) => (
    picker.dataset.rcdMultiSelectName
    || (picker.closest('[data-handler="AddAppointment"]') ? "StaffUserIds" : "NotifyStaffIds")
);

export const setupRcdMultiSelects = (root = document, afterRender = null) => {
    root.querySelectorAll(`${pickerSelector}:not([data-rcd-multi-select-ready])`).forEach((picker) => {
        picker.dataset.rcdMultiSelectReady = "true";
        picker.setAttribute("data-rcd-multi-select", "");

        const input = picker.querySelector("[data-rcd-multi-select-search], [data-person-search]");
        const menu = picker.querySelector("[data-rcd-multi-select-menu], [data-person-menu]");
        const selected = picker.querySelector("[data-rcd-multi-select-selected], [data-person-selected]");
        const toggle = picker.querySelector("[data-rcd-multi-select-toggle], [data-person-menu-toggle]");
        if (!(input instanceof HTMLInputElement) || !(menu instanceof HTMLElement) || !(selected instanceof HTMLElement)) {
            return;
        }

        const options = () => [...menu.querySelectorAll(optionSelector)];
        const selectedRows = () => [...selected.querySelectorAll("[data-rcd-multi-select-item], .rcd-multi-select__item")];
        const selectedIds = () => new Set(selectedRows().map((row) => row.dataset.id));
        let activeIndex = -1;

        input.setAttribute("role", "combobox");
        input.setAttribute("aria-autocomplete", "list");
        input.setAttribute("aria-expanded", "false");
        menu.setAttribute("role", "listbox");
        menu.setAttribute("aria-multiselectable", "true");

        options().forEach((option, index) => {
            option.classList.add("rcd-multi-select__option");
            option.dataset.initials ||= (option.dataset.name || option.dataset.email || "?").trim().slice(0, 1).toLocaleUpperCase();
            option.setAttribute("role", "option");
            option.id ||= `${picker.id || "rcd-multi-select"}-option-${index}`;
        });

        const visibleOptions = () => options().filter((option) => !option.hidden);
        const syncActive = () => {
            const visible = visibleOptions();
            activeIndex = Math.min(Math.max(activeIndex, -1), visible.length - 1);
            visible.forEach((option, index) => {
                option.classList.toggle("is-active", index === activeIndex);
                option.setAttribute("aria-selected", String(selectedIds().has(option.dataset.id)));
            });
            const active = visible[activeIndex];
            if (active) {
                input.setAttribute("aria-activedescendant", active.id);
                active.scrollIntoView({ block: "nearest" });
            } else {
                input.removeAttribute("aria-activedescendant");
            }
        };

        const refresh = () => {
            const query = input.value.trim().toLocaleLowerCase();
            const ids = selectedIds();
            let visibleCount = 0;
            options().forEach((option) => {
                const label = `${option.dataset.name || ""} ${option.dataset.email || ""}`.toLocaleLowerCase();
                const hidden = ids.has(option.dataset.id) || (query !== "" && !label.includes(query));
                option.hidden = hidden;
                if (!hidden) {
                    visibleCount += 1;
                }
            });
            const empty = menu.querySelector("[data-rcd-multi-select-empty], [data-person-empty]");
            if (empty) {
                empty.hidden = visibleCount > 0;
            }
            syncActive();
        };

        const open = () => {
            refresh();
            menu.hidden = false;
            input.setAttribute("aria-expanded", "true");
        };

        const close = () => {
            menu.hidden = true;
            input.setAttribute("aria-expanded", "false");
            input.removeAttribute("aria-activedescendant");
            activeIndex = -1;
        };

        const syncEmpty = () => {
            const empty = selected.querySelector("[data-rcd-multi-select-selected-empty], [data-person-selected-empty]");
            if (empty) {
                empty.hidden = selectedRows().length > 0;
            }
        };

        const addOption = (option) => {
            if (!(option instanceof HTMLElement) || selectedIds().has(option.dataset.id)) {
                return;
            }

            const messages = window.rcdMessages || {};
            const id = option.dataset.id || "";
            const name = option.dataset.name || option.dataset.email || messages.staffMember || "—";
            const email = option.dataset.email || "";
            const removeLabel = [messages.removeItem, name].filter(Boolean).join(" ");
            const row = document.createElement("div");
            row.className = "rcd-multi-select__item";
            row.dataset.rcdMultiSelectItem = "";
            row.dataset.id = id;
            row.innerHTML = `
                <input type="hidden" name="${escapeHtml(resolveFieldName(picker))}" value="${escapeHtml(id)}">
                <span class="rcd-avatar rcd-avatar--sm">${escapeHtml(name.trim().slice(0, 1).toLocaleUpperCase() || "?")}</span>
                <span class="rcd-multi-select__copy">
                    <strong>${escapeHtml(name)}</strong>
                    <small>${escapeHtml(email)}</small>
                </span>
                <button type="button" class="rcd-icon-button" data-rcd-multi-select-remove aria-label="${escapeHtml(removeLabel)}">
                    <i data-lucide="x"></i>
                </button>`;
            selected.append(row);
            input.value = "";
            syncEmpty();
            refresh();
            close();
            afterRender?.();
            picker.dispatchEvent(new CustomEvent("rcd:multi-select:change", {
                bubbles: true,
                detail: { values: [...selectedIds()] }
            }));
        };

        input.addEventListener("focus", open);
        input.addEventListener("input", () => {
            activeIndex = -1;
            open();
        });
        toggle?.addEventListener("click", (event) => {
            event.preventDefault();
            if (menu.hidden) {
                input.focus();
                open();
            } else {
                close();
            }
        });
        menu.addEventListener("click", (event) => {
            const option = event.target instanceof Element ? event.target.closest(optionSelector) : null;
            if (option) {
                event.preventDefault();
                addOption(option);
            }
        });
        selected.addEventListener("click", (event) => {
            const remove = event.target instanceof Element
                ? event.target.closest("[data-rcd-multi-select-remove], [data-person-remove]")
                : null;
            if (!remove) {
                return;
            }
            remove.closest("[data-rcd-multi-select-item], .rcd-multi-select__item")?.remove();
            syncEmpty();
            refresh();
            picker.dispatchEvent(new CustomEvent("rcd:multi-select:change", {
                bubbles: true,
                detail: { values: [...selectedIds()] }
            }));
        });
        picker.addEventListener("rcd:multi-select:set", (event) => {
            const values = new Set((event.detail?.values || []).map((value) => String(value)));
            selectedRows().forEach((row) => row.remove());
            options().forEach((option) => {
                if (values.has(String(option.dataset.id || ""))) {
                    addOption(option);
                }
            });
            input.value = "";
            syncEmpty();
            refresh();
            close();
        });
        picker.addEventListener("keydown", (event) => {
            if (event.key === "Escape") {
                close();
            } else if (event.key === "ArrowDown" || event.key === "ArrowUp") {
                event.preventDefault();
                open();
                const count = visibleOptions().length;
                activeIndex = count
                    ? (activeIndex + (event.key === "ArrowDown" ? 1 : -1) + count) % count
                    : -1;
                syncActive();
            } else if (event.key === "Enter" && !menu.hidden && activeIndex >= 0) {
                event.preventDefault();
                addOption(visibleOptions()[activeIndex]);
            }
        });
        document.addEventListener("pointerdown", (event) => {
            if (!picker.contains(event.target)) {
                close();
            }
        });

        syncEmpty();
        refresh();
    });
};
